// Orden de compra imprimible para el proveedor (fábrica).
// Ruta: /compra/:id/pdf — branded, con características por renglón para fabricación.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { theme } from '../styles/theme';

const BRAND = theme.brand;
const ACCENT = theme.accent;
const MUTED = theme.textMuted;
const BORDER = theme.border;

function NuledLogo({ height = 32, color = '#FFFFFF' }: { height?: number; color?: string }) {
  return (
    <svg viewBox="0 0 245.417 58.032" height={height} style={{ display: 'block', color }} aria-label="NULED">
      <path d="M3.289,0,32.517,56.97l.126.247H36.6L7.244,0Z" transform="translate(1.483)" fill="currentColor" />
      <rect width="3.517" height="57.216" transform="translate(0 0)" fill="currentColor" />
      <rect width="3.519" height="57.216" transform="translate(39.336 0)" fill="currentColor" />
      <path d="M77.875,0V35.545c0,12.339-6.013,19.134-16.93,19.134s-16.93-6.795-16.93-19.134V0H40.5V33.995c0,15.726,7.071,24.037,20.447,24.037s20.447-8.311,20.447-24.037V0Z" transform="translate(18.264 -0.001)" fill="currentColor" />
      <path d="M83.163,53.863V0H79.646V57.216h32V53.863Z" transform="translate(35.919 -0.001)" fill="currentColor" />
      <path d="M111.231,53.863V29.143h27.421V25.789H111.231V3.354h28.155V0H107.712V57.216h32.326V53.863Z" transform="translate(48.576 -0.001)" fill="currentColor" />
      <path d="M152,0H138.649V57.216H152c19.343,0,30.893-10.694,30.893-28.607S171.34,0,152,0m27.212,28.609c0,16.521-9.693,25.254-28.029,25.254h-9.013V3.355h9.013c18.336,0,28.029,8.732,28.029,25.254" transform="translate(62.528 -0.001)" fill="currentColor" />
    </svg>
  );
}

type PO = { po_number: string; supplier_id: string | null; currency: string; ordered_at: string | null; expected_at: string | null; notes: string | null; shipping: number | null; tax: number | null; subtotal: number | null; total: number | null; status: string };
type Sup = { name: string; contact_name: string | null; email: string | null; phone: string | null; country: string | null; incoterms: string | null; payment_terms: string | null; lead_time_days: number | null };
type Item = { sku: string; description: string | null; notes: string | null; quantity: number; unit_cost: number; line_total: number | null; sort_order: number | null; product_id?: string | null; variant_id?: string | null; _img?: string; _specs?: string; _variante?: string };

export function CompraPdf() {
  const { id } = useParams<{ id: string }>();
  const [po, setPo] = useState<PO | null>(null);
  const [sup, setSup] = useState<Sup | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const { data: p } = await supabase.from('purchase_orders').select('*').eq('id', id).maybeSingle();
      if (!p) { setLoading(false); return; }
      setPo(p as PO);
      const [{ data: s }, { data: its }] = await Promise.all([
        (p as PO).supplier_id
          ? supabase.from('suppliers').select('name, contact_name, email, phone, country, incoterms, payment_terms, lead_time_days').eq('id', (p as PO).supplier_id).maybeSingle()
          : Promise.resolve({ data: null }),
        supabase.from('purchase_order_items').select('product_id, variant_id, sku, description, notes, quantity, unit_cost, line_total, sort_order').eq('po_id', id).order('sort_order'),
      ]);
      setSup((s as Sup) ?? null);
      const rawItems = (its as Item[]) ?? [];
      const pids = Array.from(new Set(rawItems.map(i => i.product_id).filter(Boolean))) as string[];
      const vids = Array.from(new Set(rawItems.map(i => i.variant_id).filter(Boolean))) as string[];
      const [{ data: prods }, { data: vars }] = await Promise.all([
        pids.length ? supabase.from('products').select('id, images, specs').in('id', pids) : Promise.resolve({ data: [] }),
        vids.length ? supabase.from('product_variants').select('id, attributes').in('id', vids) : Promise.resolve({ data: [] }),
      ]);
      const pMap: Record<string, any> = {}; for (const p of (prods as any[]) ?? []) pMap[p.id] = p;
      const vMap: Record<string, any> = {}; for (const v of (vars as any[]) ?? []) vMap[v.id] = v;
      const SPEC_LABELS: [string, string][] = [['potencia','POT'],['voltaje','V'],['cct','CCT'],['cri','CRI'],['ip','IP'],['angulo','ÁNG'],['lumenes','LM'],['material','Mat'],['dimeo','Med']];
      const enriched = rawItems.map(it => {
        const p = it.product_id ? pMap[it.product_id] : null;
        const arr = Array.isArray(p?.images) ? p.images : [];
        const thumb = arr.find((x: any) => x?.is_thumbnail) ?? arr[0];
        const sp = (p?.specs ?? {}) as any;
        const specsTxt = SPEC_LABELS.filter(([k]) => sp[k] != null && String(sp[k]) !== '').map(([k, lbl]) => `${lbl}: ${sp[k]}`).join('  ·  ');
        const attrs = (it.variant_id ? vMap[it.variant_id]?.attributes : null) ?? {};
        const variante = Object.values(attrs).filter(Boolean).join(' · ');
        return { ...it, _img: thumb?.url ?? '', _specs: specsTxt, _variante: variante };
      });
      setItems(enriched);
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => { if (po) { const prev = document.title; document.title = `${po.po_number} - OC NULED`; return () => { document.title = prev; }; } }, [po]);

  if (loading) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Cargando orden…</div>;
  if (!po) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Orden no encontrada</div>;

  const cur = po.currency || 'USD';
  const money = (n: number | null | undefined) => `$${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
  const date = (d: string | null) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  return (
    <>
      <style>{`
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        @media print {
          @page { size: letter; margin: 0; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden !important; }
          .oc-wrap, .oc-wrap * { visibility: visible !important; }
          .no-print, .no-print * { visibility: hidden !important; display: none !important; }
          .oc-wrap { max-width: none !important; box-shadow: none !important; margin: 0 !important; }
          .oc-content { padding: 0.5in 0.5in 0.42in !important; }
          tr { page-break-inside: avoid; }
        }
        body { margin: 0; background: #f5f5f5; font-family: ${theme.font.sans}; }
        .oc-wrap { max-width: 8.5in; margin: 0 auto; background: white; color: ${theme.text}; font-size: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
        .oc-content { padding: 0.5in; }
        .itm { width: 100%; border-collapse: collapse; }
        .itm th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: ${MUTED}; padding: 8px 8px; border-bottom: 2px solid ${BRAND}; }
        .itm td { padding: 9px 8px; border-bottom: 1px solid ${BORDER}; vertical-align: top; }
      `}</style>

      <div className="no-print" style={{ background: BRAND, color: 'white', padding: '14px 24px', display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', fontFamily: theme.font.sans }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <NuledLogo height={20} color="white" />
          <span style={{ fontSize: 11, opacity: 0.7 }}>Orden de compra · {po.po_number}</span>
        </div>
        <button onClick={() => window.print()} style={{ background: ACCENT, color: BRAND, border: 'none', padding: '8px 18px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer', fontFamily: theme.font.sans }}>📥 Descargar / Imprimir</button>
      </div>

      <div className="oc-wrap">
        <div style={{ background: BRAND, color: 'white', padding: '0.4in 0.5in 0.3in', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <NuledLogo height={28} color="white" />
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.65)', marginTop: 8, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>OMM Technologies · NULED</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: 2 }}>Orden de compra</div>
            <div style={{ fontFamily: theme.font.mono, fontSize: 15, fontWeight: 700, marginTop: 4 }}>{po.po_number}</div>
          </div>
        </div>
        <div style={{ height: 4, background: ACCENT }} />

        <div className="oc-content">
          {/* Proveedor + datos */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 22 }}>
            <div>
              <div style={{ fontSize: 9, color: MUTED, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>Proveedor</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: BRAND }}>{sup?.name ?? '—'}</div>
              {sup?.contact_name && <div style={{ fontSize: 11, color: theme.text, marginTop: 2 }}>{sup.contact_name}</div>}
              {sup?.email && <div style={{ fontSize: 11, color: MUTED }}>{sup.email}</div>}
              {sup?.phone && <div style={{ fontSize: 11, color: MUTED }}>{sup.phone}</div>}
              {sup?.country && <div style={{ fontSize: 11, color: MUTED }}>{sup.country}</div>}
            </div>
            <div style={{ fontSize: 11 }}>
              <Row k="Fecha de orden" v={date(po.ordered_at)} />
              <Row k="Entrega estimada (ETA)" v={date(po.expected_at)} />
              {sup?.lead_time_days != null && <Row k="Lead time" v={`${sup.lead_time_days} días`} />}
              {sup?.incoterms && <Row k="Incoterms" v={sup.incoterms} />}
              {sup?.payment_terms && <Row k="Términos de pago" v={sup.payment_terms} />}
              <Row k="Moneda" v={cur} />
            </div>
          </div>

          {/* Items */}
          <table className="itm">
            <thead>
              <tr>
                <th style={{ width: 66 }}>Foto</th>
                <th style={{ width: '16%' }}>SKU / Modelo</th>
                <th>Descripción y características (fabricar según esto)</th>
                <th style={{ textAlign: 'right', width: 44 }}>Cant</th>
                <th style={{ textAlign: 'right', width: 82 }}>Costo unit</th>
                <th style={{ textAlign: 'right', width: 90 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td style={{ textAlign: 'center' }}>
                    {it._img ? <img src={it._img} alt="" style={{ width: 54, height: 54, objectFit: 'contain' }} /> : null}
                  </td>
                  <td style={{ fontFamily: theme.font.mono, fontSize: 10, color: BRAND, fontWeight: 700 }}>{it.sku}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: BRAND }}>{it.description}</div>
                    {it._variante && <div style={{ fontSize: 10, color: theme.text, marginTop: 2 }}>{it._variante}</div>}
                    {it._specs && <div style={{ fontSize: 9.5, color: theme.text, marginTop: 3, lineHeight: 1.5 }}>{it._specs}</div>}
                    {it.notes && <div style={{ fontSize: 9.5, color: theme.textMuted, marginTop: 3, lineHeight: 1.5 }}>{it.notes}</div>}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{it.quantity}</td>
                  <td style={{ textAlign: 'right' }}>{money(it.unit_cost)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: BRAND }}>{money(it.line_total ?? it.quantity * it.unit_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totales */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <div style={{ width: 260, fontSize: 12 }}>
              <TotRow k="Subtotal" v={money(po.subtotal)} />
              {po.shipping ? <TotRow k="Envío / flete" v={money(po.shipping)} /> : null}
              {po.tax ? <TotRow k="Impuestos / aduana" v={money(po.tax)} /> : null}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', marginTop: 6, borderTop: `2px solid ${BRAND}`, fontWeight: 700, fontSize: 15, color: BRAND }}>
                <span>Total</span><span>{money(po.total)}</span>
              </div>
            </div>
          </div>

          {po.notes && (
            <div style={{ marginTop: 20, padding: 12, background: theme.bgSoft, border: `1px solid ${BORDER}`, fontSize: 11 }}>
              <div style={{ fontSize: 9, color: MUTED, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>Notas</div>
              <div style={{ whiteSpace: 'pre-line' }}>{po.notes}</div>
            </div>
          )}

          <div style={{ marginTop: 24, paddingTop: 14, borderTop: `1px solid ${BORDER}`, textAlign: 'center' }}>
            <NuledLogo height={12} color={BRAND} />
            <div style={{ fontSize: 8, color: MUTED, marginTop: 8 }}>OMM Technologies · NULED · compras@nuled.mx · Favor de confirmar recepción y fecha de embarque.</div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${theme.bgImage}` }}>
      <span style={{ color: MUTED }}>{k}</span><span style={{ fontWeight: 600, color: theme.brand }}>{v}</span>
    </div>
  );
}
function TotRow({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <span style={{ color: MUTED }}>{k}</span><span style={{ fontWeight: 600 }}>{v}</span>
    </div>
  );
}
